<template>
  <div class="announcement-page">
    <div class="announcement-container" :dir="lang === 'ar' ? 'rtl' : 'ltr'">

      <!-- Cover Image -->
      <div class="cover-image">
        <img src="https://quali-d.com/blog/image14.jpg" alt="WhatsApp Business API Pricing Update" />
      </div>

      <!-- Card Body -->
      <div class="card-body">

      <!-- Top Bar: badges + language toggle -->
      <div class="top-bar">
        <div class="badge-row">
          <span class="badge badge-warning">{{ lang === 'ar' ? 'تنويه هام' : 'Important Update' }}</span>
          <span class="badge badge-meta">{{ lang === 'ar' ? 'سياسة Meta' : 'Meta Platform Policy' }}</span>
        </div>
        <div class="lang-toggle">
          <button
            class="lang-btn"
            :class="{ active: lang === 'en' }"
            @click="lang = 'en'"
          >EN</button>
          <button
            class="lang-btn"
            :class="{ active: lang === 'ar' }"
            @click="lang = 'ar'"
          >عر</button>
        </div>
      </div>

      <!-- English Section -->
      <section v-if="lang === 'en'" class="content-section" lang="en">
        <h1 class="announcement-title">Important Update Regarding<br>WhatsApp Business API Pricing</h1>
        <p class="salutation">Dear Valued Customer,</p>

        <p class="body-text">
          We would like to inform you about an upcoming pricing update announced by
          <strong>Meta</strong> that will take effect on
          <span class="date-highlight">October 1, 2026</span>.
        </p>

        <div class="info-block">
          <h2 class="block-title">What is changing?</h2>
          <p class="body-text">
            Until now, when <strong>your customer initiated a conversation with your business on WhatsApp</strong>,
            you were able to <strong>reply freely during the following 24-hour customer service window</strong>
            without Meta charging for those replies.
          </p>
          <div class="highlight-box highlight-box--warning">
            Starting <span class="date-highlight">October 1, 2026</span>,
            <strong>every reply your business sends during that 24-hour window will become billable by Meta</strong>,
            even if the customer initiated the conversation.
          </div>
          <p class="body-text" style="margin-top: 16px;">
            This is a <strong>platform-wide policy change introduced by Meta</strong> and applies to
            <strong>all official WhatsApp Business API providers</strong>, including Quali-D.
          </p>
        </div>

        <div class="info-block">
          <h2 class="block-title">What does this mean for you?</h2>
          <div class="highlight-box highlight-box--info">
            At this stage, <strong>Meta has not yet published the new pricing rates</strong>. The official rates
            are expected to be announced by <span class="date-highlight">September 1, 2026</span>.
          </div>
          <p class="body-text" style="margin-top: 16px;">
            The good news is that there is still time before these changes take effect, and our team has already
            started working on strategies to help minimize their impact.
          </p>
          <p class="body-text">Over the coming weeks, the Quali-D team will:</p>
          <ul class="action-list">
            <li>Review your current messaging usage.</li>
            <li>Optimize automation and conversation flows where possible to reduce unnecessary messages.</li>
            <li>Provide you with an estimate of the expected impact on your account once Meta publishes the official rates.</li>
          </ul>
        </div>

        <div class="action-notice">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
          </svg>
          <span><strong>No action is required from you at this time.</strong> We will continue monitoring Meta's
          announcements and keep you informed every step of the way.</span>
        </div>

        <p class="body-text">If you have any questions, our team is always here to help.</p>

        <p class="closing">Thank you for your continued trust in <strong>Quali-D</strong>.</p>
      </section>

      <!-- Arabic Section -->
      <section v-if="lang === 'ar'" class="content-section content-section--rtl" lang="ar">
        <h1 class="announcement-title">تنويه هام بشأن تحديث أسعار<br>WhatsApp Business API من Meta</h1>
        <p class="salutation">عميلنا العزيز،</p>

        <p class="body-text">
          نود إبلاغكم بأن شركة <strong>Meta</strong> أعلنت عن تحديث جديد في آلية تسعير
          <strong>WhatsApp Business API</strong>، وسيبدأ تطبيقه اعتبارًا من
          <span class="date-highlight">1 أكتوبر 2026</span>.
        </p>

        <div class="info-block">
          <h2 class="block-title">ما الذي سيتغير؟</h2>
          <p class="body-text">
            حتى الآن، عندما <strong>يقوم العميل بإرسال أول رسالة إلى نشاطكم التجاري عبر واتساب</strong>،
            كان بإمكانكم <strong>الرد على العميل بحرية خلال نافذة خدمة العملاء الممتدة لمدة 24 ساعة</strong>
            دون احتساب أي رسوم من Meta على تلك الردود.
          </p>
          <div class="highlight-box highlight-box--warning">
            اعتبارًا من <span class="date-highlight">1 أكتوبر 2026</span>،
            <strong>ستصبح كل رسالة يرسلها نشاطكم التجاري إلى العميل خلال هذه الفترة خاضعة لرسوم من Meta</strong>،
            حتى لو كان العميل هو من بدأ المحادثة.
          </div>
          <p class="body-text" style="margin-top: 16px;">
            ونؤكد أن هذا <strong>تحديث رسمي من شركة Meta</strong>، ويُطبق على
            <strong>جميع مزودي خدمة WhatsApp Business API المعتمدين</strong>، بما في ذلك منصة Quali-D.
          </p>
        </div>

        <div class="info-block">
          <h2 class="block-title">ماذا يعني ذلك بالنسبة لكم؟</h2>
          <div class="highlight-box highlight-box--info">
            حتى الآن، <strong>لم تُعلن Meta عن الأسعار الجديدة</strong>، ومن المتوقع أن يتم نشر الأسعار الرسمية
            <span class="date-highlight">بحلول 1 سبتمبر 2026</span>.
          </div>
          <p class="body-text" style="margin-top: 16px;">
            والخبر الجيد أن هناك متسعًا من الوقت قبل بدء تطبيق هذه التغييرات، وقد بدأ فريق
            <strong>Quali-D</strong> بالفعل العمل على دراسة أفضل الطرق لتقليل تأثيرها قدر الإمكان.
          </p>
          <p class="body-text">وخلال الأسابيع القادمة سيقوم فريقنا بـ:</p>
          <ul class="action-list">
            <li>مراجعة استخدام الرسائل لكل حساب.</li>
            <li>تحسين تدفقات المحادثات والأتمتة لتقليل الرسائل غير الضرورية كلما أمكن.</li>
            <li>تزويدكم بتقدير للتأثير المتوقع على حسابكم فور إعلان Meta للأسعار الرسمية.</li>
          </ul>
        </div>

        <div class="action-notice">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
          </svg>
          <span><strong>لا يتطلب الأمر أي إجراء من جانبكم في الوقت الحالي.</strong> وسنواصل متابعة تحديثات Meta
          وإطلاعكم على كل جديد أولًا بأول، مع العمل على تقديم أفضل الحلول الممكنة لتقليل أي تكاليف إضافية.</span>
        </div>

        <p class="body-text">إذا كانت لديكم أي استفسارات، يسعد فريقنا دائمًا بمساعدتكم.</p>

        <p class="closing">شكرًا لثقتكم المستمرة في <strong>Quali-D</strong>.</p>
      </section>

      <!-- Footer -->
      <div class="footer-actions">
        <button class="btn-back" @click="goBack">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"
            :style="lang === 'ar' ? 'transform: scaleX(-1)' : ''">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          {{ lang === 'ar' ? 'العودة إلى لوحة التحكم' : 'Back to Dashboard' }}
        </button>
      </div>

      </div><!-- end card-body -->
    </div>
  </div>
</template>

<script>
export default {
  name: 'AnnouncementView',
  data () {
    return {
      lang: 'en'
    }
  },
  methods: {
    goBack () {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push('/')
      }
    }
  }
}
</script>

<style scoped>
.announcement-page {
  min-height: 100vh;
  background: linear-gradient(160deg, #0f172a 0%, #1e293b 60%, #0f172a 100%);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 20px;
}

.announcement-container {
  background: #ffffff;
  border-radius: 20px;
  max-width: 780px;
  width: 100%;
  padding: 0;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}

.cover-image {
  width: 100%;
  aspect-ratio: 16 / 6;
  overflow: hidden;
}

.cover-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.card-body {
  padding: 40px 52px 48px;
}

/* Top bar: badges + toggle */
.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}

.badge-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 4px 11px;
  border-radius: 20px;
}

.badge-warning {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
}

.badge-meta {
  background: #ede9fe;
  color: #5b21b6;
  border: 1px solid #c4b5fd;
}

/* Language Toggle */
.lang-toggle {
  display: flex;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
  flex-shrink: 0;
}

.lang-btn {
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.18s ease;
  letter-spacing: 0.03em;
}

.lang-btn.active {
  background: #0f172a;
  color: #f59e0b;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.lang-btn:not(.active):hover {
  background: #e2e8f0;
  color: #334155;
}

/* Icon */
.announcement-icon {
  margin-bottom: 24px;
}

/* Content Sections */
.content-section {
  animation: fadeSlide 0.22s ease;
}

.content-section--rtl {
  font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
}

@keyframes fadeSlide {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.announcement-title {
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 20px;
  line-height: 1.35;
}

.salutation {
  font-size: 16px;
  color: #374151;
  margin: 0 0 16px;
  font-weight: 500;
}

.body-text {
  font-size: 15px;
  color: #4b5563;
  line-height: 1.75;
  margin: 0 0 16px;
}

/* Info Block */
.info-block {
  margin: 24px 0;
}

.block-title {
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 14px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
}

/* Highlight Boxes */
.highlight-box {
  border-radius: 10px;
  padding: 16px 20px;
  font-size: 15px;
  line-height: 1.7;
  margin-top: 12px;
}

.highlight-box--warning {
  background: #fffbeb;
  border-inline-start: 4px solid #f59e0b;
  color: #78350f;
}

.highlight-box--info {
  background: #eff6ff;
  border-inline-start: 4px solid #3b82f6;
  color: #1e3a5f;
}

/* Date Highlight */
.date-highlight {
  display: inline-block;
  background: #0f172a;
  color: #f59e0b;
  font-weight: 700;
  padding: 1px 8px;
  border-radius: 5px;
  font-size: 14px;
}

/* Action List */
.action-list {
  margin: 0 0 16px;
  padding-inline-start: 20px;
  color: #4b5563;
  font-size: 15px;
  line-height: 1.75;
}

.action-list li {
  margin-bottom: 6px;
}

/* Action Notice */
.action-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 10px;
  padding: 16px 20px;
  margin: 20px 0;
  color: #14532d;
  font-size: 15px;
  line-height: 1.65;
}

.action-notice svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: #16a34a;
}

.closing {
  font-size: 15px;
  color: #374151;
  margin: 20px 0 0;
  font-weight: 500;
}

/* Footer */
.footer-actions {
  margin-top: 40px;
  padding-top: 28px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #0f172a;
  color: #ffffff;
  border: none;
  padding: 13px 28px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-back:hover {
  background: #1e293b;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.3);
}

/* Responsive */
@media (max-width: 640px) {
  .announcement-page {
    padding: 24px 12px;
  }

  .card-body {
    padding: 24px 20px 32px;
  }

  .announcement-title {
    font-size: 20px;
  }

  .body-text,
  .highlight-box {
    font-size: 14px;
  }

  .top-bar {
    flex-direction: row;
    align-items: center;
  }
}
</style>

<style>
/* Dark mode support */
.dark .announcement-page {
  background: linear-gradient(160deg, #020617 0%, #0f172a 60%, #020617 100%);
}

.dark .announcement-container {
  background: #1e2535;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.6);
}

.dark .lang-toggle {
  background: #0f172a;
}

.dark .lang-btn:not(.active):hover {
  background: #1e293b;
  color: #cbd5e1;
}

.dark .announcement-title {
  color: #f1f5f9;
}

.dark .salutation,
.dark .closing {
  color: #cbd5e1;
}

.dark .body-text {
  color: #94a3b8;
}

.dark .action-list {
  color: #94a3b8;
}

.dark .block-title {
  color: #e2e8f0;
  border-bottom-color: #334155;
}

.dark .footer-actions {
  border-top-color: #334155;
}

.dark .btn-back {
  background: #f1f5f9;
  color: #0f172a;
}

.dark .btn-back:hover {
  background: #e2e8f0;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.dark .badge-warning {
  background: #451a03;
  color: #fcd34d;
  border-color: #78350f;
}

.dark .badge-meta {
  background: #2e1065;
  color: #c4b5fd;
  border-color: #5b21b6;
}

.dark .highlight-box--warning {
  background: #1c1000;
  color: #fde68a;
}

.dark .highlight-box--info {
  background: #0c1a2e;
  color: #93c5fd;
}

.dark .action-notice {
  background: #052e16;
  border-color: #166534;
  color: #86efac;
}
</style>
